import { 
  parse, 
  parseISO, 
  format, 
  isSameDay as dateFnsIsSameDay,
  addDays,
  startOfDay,
  isValid
} from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

// Get timezone from environment or default to Pacific
const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/Los_Angeles';

/**
 * Get the current date/time in the configured timezone
 * Returns a Date object representing "now" in Pacific time
 */
export const getTodayInTimezone = () => {
  const now = new Date();
  return toZonedTime(now, TIMEZONE);
};

/**
 * Get today at midnight in the configured timezone
 * Useful for date comparisons
 */
export const getTodayStart = () => {
  return startOfDay(getTodayInTimezone());
};

/**
 * Parse delivery date string in various formats
 * Handles: "MM/DD/YYYY", "M/D/YYYY", "YYYY-MM-DD", "Month DD, YYYY"
 * Returns Date object or null if invalid
 */
export const parseDeliveryDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const trimmed = dateString.trim();
  
  // Try common formats
  const formats = [
    'MM/dd/yyyy',   // 02/12/2026
    'M/d/yyyy',     // 2/12/2026
    'MM-dd-yyyy',   // 02-12-2026
    'yyyy-MM-dd',   // 2026-02-12
    'EEEE, MMMM dd, yyyy', // Thursday, February 12, 2026
    'MMMM dd, yyyy', // February 12, 2026
    'MMMM d, yyyy',  // February 12, 2026
    'MMM dd, yyyy',  // Feb 12, 2026
    'MMM d, yyyy',   // Feb 12, 2026
    'MM/dd/yy',     // 02/12/26
  ];

  // Try parsing with each format
  for (const formatStr of formats) {
    try {
      const parsed = parse(trimmed, formatStr, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    } catch (e) {
      // Try next format
      continue;
    }
  }

  // Try ISO format
  try {
    const parsed = parseISO(trimmed);
    if (isValid(parsed)) {
      return parsed;
    }
  } catch (e) {
    console.error('Date parse failed:', e);
  }

  console.warn(`Unable to parse date: "${dateString}"`);
  return null;
};

/**
 * Compare two dates (ignoring time)
 * Returns true if both dates are the same day
 */
export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  
  const d1 = typeof date1 === 'string' ? parseDeliveryDate(date1) : date1;
  const d2 = typeof date2 === 'string' ? parseDeliveryDate(date2) : date2;
  
  if (!d1 || !d2) return false;
  
  return dateFnsIsSameDay(d1, d2);
};

/**
 * Format date for display in tabs
 * Returns: "Today" or "M/D" format
 */
export const formatDateForTab = (date) => {
  if (!date) return '';
  
  const today = getTodayStart();
  const targetDate = startOfDay(date);
  
  // Check if it's today
  if (dateFnsIsSameDay(today, targetDate)) {
    return 'Today';
  }
  
  // Format as "M/D" using local format (no timezone conversion)
  return format(date, 'M/d');
};

/**
 * Format date as yyyy/MM/dd
 */
export const formatDateShort = (date) => {
  if (!date) return '';
  
  const parsed = typeof date === 'string' ? parseDeliveryDate(date) : date;
  if (!parsed) return date; // Return original if can't parse
  
  return formatInTimeZone(parsed, TIMEZONE, 'yyyy/MM/dd');
};

/**
 * Format YYYY-MM-DD date to display format (yyyy/mm/dd)
 * Simply reformats the date string without timezone conversion
 * since delivery dates are date-only values
 */
export const formatDeliveryDateDisplay = (dateString) => {
  if (!dateString) return '';
  
  // If already in YYYY-MM-DD format, just convert slashes
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString.replace(/-/g, '/');
  }
  
  // For other formats, parse and reformat
  const parsed = parseDeliveryDate(dateString);
  if (!parsed) return dateString;
  
  // Use local format to preserve the date as-is
  return format(parsed, 'yyyy/MM/dd');
};

/**
 * Convert Date object to YYYY-MM-DD string for backend storage
 */
export const formatDateForBackend = (date) => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
};

/**
 * Format a timestamp as a short date-time WITH the timezone abbreviation
 * (e.g. "Jul 25, 12:28 AM PDT"). Accepts a tz-aware ISO string or Date. Uses the
 * shop's configured `timeZone` (IANA) when given, else the default TIMEZONE.
 */
export const formatDateTimeWithZone = (input, timeZone) => {
  if (!input) return '';
  try {
    const parsed = typeof input === 'string' ? parseISO(input) : input;
    if (!isValid(parsed)) return '';
    return formatInTimeZone(parsed, timeZone || TIMEZONE, 'MMM d, h:mm a zzz');
  } catch {
    return '';
  }
};

/**
 * Generate array of next N days starting from today
 * Returns array of Date objects
 */
export const getNextNDays = (n = 7) => {
  const today = getTodayStart();
  return Array.from({ length: n }, (_, i) => addDays(today, i));
};

/**
 * Get a specific date offset from today
 * Example: getDateOffset(0) = today, getDateOffset(1) = tomorrow
 */
export const getDateOffset = (offsetDays) => {
  const today = getTodayStart();
  return addDays(today, offsetDays);
};

/**
 * Check if a date string is today
 */
export const isToday = (dateString) => {
  const date = parseDeliveryDate(dateString);
  if (!date) return false;
  
  const today = getTodayStart();
  return isSameDay(date, today);
};

/**
 * Get the configured timezone
 */
export const getTimezone = () => TIMEZONE;
